import React from 'react';
import { Mail } from 'lucide-react';
import { Button, CompactError, RobbiePopup, SegmentedProgress } from '../ui';
import { ResearchSummary } from '../../types/bom.types';
import { etaPartsOf } from './researchProgress';

interface ResearchProgressPopupProps {
  open: boolean;
  summary: ResearchSummary;
  /** Only when the server will really send it. */
  emailPromise: boolean;
  email?: string | null;
  onDontEmail: () => void;
  dontEmailBusy: boolean;
  dontEmailFailed: boolean;
  onClose: () => void;
  onGoToDashboard: () => void;
}

/** The summary pop-up a buyer meets when they land on discovery mid-research. */
export const ResearchProgressPopup: React.FC<ResearchProgressPopupProps> = ({
  open,
  summary,
  emailPromise,
  email,
  onDontEmail,
  dontEmailBusy,
  dontEmailFailed,
  onClose,
  onGoToDashboard,
}) => {
  const eta = etaPartsOf(summary);
  return (
    <RobbiePopup
      open={open}
      size="summary"
      onClose={onClose}
      title={`Finding suppliers for ${summary.total} component${summary.total === 1 ? '' : 's'}`}
      sub={
        eta ? (
          <>
            About <b className="text-ink">{eta.minutes} minutes</b> more
            {eta.doneAround && (
              <>
                {' · done around '}
                <b className="text-ink">{eta.doneAround}</b>
              </>
            )}
          </>
        ) : undefined
      }
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Look at the parts so far
          </Button>
          <Button onClick={onGoToDashboard}>Go to dashboard</Button>
        </>
      }
    >
      <SegmentedProgress
        counts={{ done: summary.done, running: summary.researching, waiting: summary.waiting }}
        showLegend
        thick
      />
      {emailPromise && (
        <div className="flex items-start gap-s3 rounded-card bg-neutral-bg px-s5 py-s4 text-body-md text-ink-body">
          <Mail aria-hidden="true" className="mt-[3px] h-[15px] w-[15px] shrink-0 text-robbie" />
          <p className="m-0">
            You don't need to wait here — I'll email {email ? <b className="text-ink">{email}</b> : 'you'} when
            they're all done.{' '}
            <button
              type="button"
              onClick={onDontEmail}
              disabled={dontEmailBusy}
              className="font-semibold text-action hover:underline disabled:opacity-60"
            >
              Don't email me
            </button>
          </p>
        </div>
      )}
      {emailPromise && dontEmailFailed && (
        <CompactError layout="tiny" title="That didn't save." message="I'll still email you." action={{ label: 'Try again', onClick: onDontEmail }} />
      )}
    </RobbiePopup>
  );
};

export default ResearchProgressPopup;
